import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import ShieldIcon from '@mui/icons-material/Shield';
import DescriptionIcon from '@mui/icons-material/Description';
import AppDataGrid from '@/components/common/AppDataGrid';
import { store } from '@/service/store';
import { useSignals } from "@preact/signals-react/runtime";
import { formatDate } from '@/utils/formatters';
import AssignmentDetailDialog from '@/components/events/AssignmentDetailDialog';
import TseDetailsDialog from '@/components/events/TseDetailsDialog';
import { deviceTypeDict } from '@/constants';

interface CustomerAssignmentsTabProps {
  customerId: string;
}

const CustomerAssignmentsTab: React.FC<CustomerAssignmentsTabProps> = ({ customerId }) => {
  useSignals();
  const [assignments, setAssignments] = useState<any[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<any | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tseDialogOpen, setTseDialogOpen] = useState(false);

  const { t } = useTranslation();

  let customer = store.customers.get(customerId);
  const loading = store.customers.isLoading.value || customer?.installations.isLoading.value;

  const handleInfoClick = (assignment: any) => {
    setSelectedAssignment(assignment);
    setDetailDialogOpen(true);
  };

  const handleTseDetailsClick = (assignment: any) => {
    setSelectedAssignment(assignment);
    setTseDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  const handleCloseTseDialog = () => {
    setTseDialogOpen(false);
  };

  const handleCertificateClick = (assignment: any) => {
    const url = `${import.meta.env.VITE_BASE_URL}/installations/${assignment.uuid}/tse-certification-pdf`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const loadAssignments = async () => {
      if (!customer) {
        await store.customers.load();
        customer = store.customers.get(customerId);
      }
      try {
        await customer.installations.load();
        setAssignments(customer.installations.data.value.filter((assignment: any) => assignment.licenseNumber !== null) || []);
      } catch (error) {
        console.error('Error loading assignments:', error);
      }
    };

    loadAssignments();
  }, [customerId]);

  const columns: GridColDef[] = [
    {
      field: 'deviceType',
      headerName: t('assignments.deviceModel'),
      minWidth: 130,
      valueGetter: (value: string) => deviceTypeDict[value] || value,
    },
    {
      field: 'deviceManufacturerNumber',
      headerName: t('assignments.deviceSerial'),
      minWidth: 160,
    },
    {
      field: 'licenseNumber',
      headerName: t('assistant.vehicleIdentificationStep.licenseNumber'),
      minWidth: 130,
    },
    {
      field: 'carVin',
      headerName: t('assistant.vehicleIdentificationStep.vin'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'installedAt',
      headerName: t('assignments.installationDate'),
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: any) => formatDate(value, false),
    },
    {
      field: 'removedAt',
      headerName: t('assignments.removedAt'),
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: any) => formatDate(value, false),
    },
    {
      field: 'isActive',
      headerName: t('assignments.status'),
      flex: 1,
      minWidth: 60,
      valueGetter: (value: object | null, row: any) => row.removedAt === null,
      valueFormatter: (value: object | null) => value ? t('common.status.active') : t('common.status.inactive')
    },
    {
      field: 'actions',
      headerName: t('events.actions'),
      sortable: false,
      width: 160, // Width to accommodate three buttons
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={t('events.viewDetails')}>
            <IconButton
              onClick={() => handleInfoClick(params.row)}
              size="small"
              color={"primary"}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('events.viewTseDetails')}>
            <IconButton
              onClick={() => handleTseDetailsClick(params.row)}
              size="small"
              color={"secondary"}
            >
              <ShieldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('devices.actions.printCertificate')}>
            <IconButton
              onClick={() => handleCertificateClick(params.row)}
              size="small"
              color={"primary"}
            >
              <DescriptionIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (assignments.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{t('assignments.noAssignments')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <AppDataGrid
        rows={assignments}
        columns={columns}
        getRowId={(row) => row.id || row.uuid}
        loading={loading}
        autoHeight={false}
        sx={{ height: '100%' }}
      />
      {selectedAssignment && (
        <>
          <AssignmentDetailDialog
            assignment={{...selectedAssignment, customerNumber: customerId}}
            open={detailDialogOpen}
            onClose={handleCloseDetailDialog}
          />
          <TseDetailsDialog
            installationUuid={selectedAssignment.uuid}
            open={tseDialogOpen}
            onClose={handleCloseTseDialog}
          />
        </>
      )}
    </Box>
  );
};

export default CustomerAssignmentsTab;
